import { StateMachine } from "@/core/arch/state_machine";
import type { JobRecord, JobStatus } from "./types";

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * Why a supplied email will or will not be paid. `will_pay` and `will_revive`
 * are the values that move money; the rest are reported so the operator can act
 * on them.
 */
export type BackfillDisposition =
  | "will_pay"
  | "will_revive"
  | "already_paid"
  | "paid_externally"
  | "wrong_stage"
  | "no_merchant"
  | "no_business"
  | "no_destination"
  | "duplicate"
  | "lookup_failed";

export interface BackfillCandidate {
  email: string;
  userId: string | null;
  businessId: string | null;
  businessName: string | null;
  stage: string | null;
  hasDestination: boolean;
  disposition: BackfillDisposition;
  detail: string | null;
}

export interface ResolvedBackfill {
  candidates: BackfillCandidate[];
  duplicates: string[];
  payableCount: number;
  /** The subset of `payableCount` whose closed track has to be reopened first. */
  reviveCount: number;
  totalAmountCents: number;
}

// ── State ────────────────────────────────────────────────────────────────────

export type ActivationBonusBackfillPhase =
  | { kind: "composing" }
  | { kind: "resolving" }
  | { kind: "resolved"; resolved: ResolvedBackfill }
  | { kind: "kicking_off"; resolved: ResolvedBackfill }
  | { kind: "watching"; job: JobRecord; resolved: ResolvedBackfill }
  | { kind: "cancelling"; job: JobRecord; resolved: ResolvedBackfill }
  /**
   * `job` is set when the failure happened while a run was already live — a
   * dropped subscription or a failed cancel. The distinction is load-bearing:
   * returning to the email form from there would let the operator enqueue a
   * second payout over merchants the first run may still be paying.
   */
  | { kind: "error"; message: string; job?: JobRecord };

export interface ActivationBonusBackfillState {
  phase: ActivationBonusBackfillPhase;
  emailsInput: string;
  /**
   * Opt-in to reopening `expired` and `failed` tracks. Off by default: widening
   * what the run treats as payable is a spending decision, and an operator
   * pasting a list for the `not_started` backlog must not reopen closed tracks
   * as a side effect.
   */
  reviveClosedTracks: boolean;
}

// ── Repository interface ─────────────────────────────────────────────────────

export interface ActivationBonusBackfillRepository {
  resolve(
    emails: string[],
    reviveClosedTracks: boolean,
  ): Promise<ResolvedBackfill>;
  kickoff(emails: string[], reviveClosedTracks: boolean): Promise<string>;
  findRunningJob(): Promise<JobRecord | null>;
  cancelJob(jobId: string): Promise<void>;
  subscribeToJob(
    jobId: string,
    onUpdate: (job: JobRecord) => void,
    onError: (err: Error) => void,
  ): () => void;
}

// ── BLoC ─────────────────────────────────────────────────────────────────────

const JOB_TYPE = "activation-bonus-backfill";

/** Mirrors the backend's ceiling so the UI refuses before the mutation does. */
export const MAX_EMAILS_PER_RUN = 1000;

export function splitEmails(raw: string): string[] {
  const seen = new Set<string>();
  const emails: string[] = [];
  for (const token of raw.split(/[\s,;]+/)) {
    const email = token.trim().toLowerCase();
    if (!email || seen.has(email)) continue;
    seen.add(email);
    emails.push(email);
  }
  return emails;
}

/**
 * Two-phase backfill: resolve the pasted emails to merchants and show what a
 * run would do, then enqueue the job against the *resolved* businesses.
 *
 * Resolution is not a formality. It is the only thing standing between a
 * mistyped list and real transfers, so the payable set shown on screen is the
 * set the job is given — nothing is re-derived from the textarea at kickoff.
 */
export class ActivationBonusBackfillBloc extends StateMachine<ActivationBonusBackfillState> {
  private _unsubscribe: (() => void) | null = null;
  private _disposed = false;

  constructor(private readonly repo: ActivationBonusBackfillRepository) {
    super({
      phase: { kind: "composing" },
      emailsInput: "",
      reviveClosedTracks: false,
    });
    this.appendDisposeCallback(this.teardown);
  }

  private readonly teardown = (): void => {
    this._disposed = true;
    this._detach();
  };

  /**
   * Returns a disposed bloc to service, matching JobBloc, JobDetailBloc and
   * JobHistoryBloc.
   *
   * `useStateMachine` disposes on effect cleanup but keeps the same instance in
   * its ref, so React StrictMode's mount/cleanup/mount replay hands the view a
   * bloc that has already been torn down, and `_attach` silently no-ops. This
   * one issues Stripe transfers, so the failure mode is a confirmed backfill
   * paying out while the sheet sits on its spinner. Re-registering matters as
   * much as clearing the flag: `dispose` consumes its callbacks, so without it
   * a later real unmount would no longer close the subscription.
   */
  private rearm(): void {
    this._disposed = false;
    this.appendDisposeCallback(this.teardown);
  }

  setEmailsInput(value: string): void {
    this.setState({ ...this.state, emailsInput: value });
  }

  setReviveClosedTracks(value: boolean): void {
    this.setState({ ...this.state, reviveClosedTracks: value });
  }

  async resumeIfRunning(): Promise<void> {
    if (this._disposed) this.rearm();
    try {
      const job = await this.repo.findRunningJob();
      if (job) {
        this._attach(job, {
          candidates: [],
          duplicates: [],
          payableCount: 0,
          reviveCount: 0,
          totalAmountCents: 0,
        });
      }
    } catch {
      // Non-fatal — the operator still sees the compose step.
    }
  }

  async resolve(): Promise<void> {
    const emails = splitEmails(this.state.emailsInput);

    if (emails.length === 0) {
      this.setState({
        ...this.state,
        phase: { kind: "error", message: "Paste at least one email address." },
      });
      return;
    }
    if (emails.length > MAX_EMAILS_PER_RUN) {
      this.setState({
        ...this.state,
        phase: {
          kind: "error",
          message: `At most ${MAX_EMAILS_PER_RUN} emails per run — received ${emails.length}.`,
        },
      });
      return;
    }

    this.setState({ ...this.state, phase: { kind: "resolving" } });
    try {
      const resolved = await this.repo.resolve(
        emails,
        this.state.reviveClosedTracks,
      );
      this.setState({ ...this.state, phase: { kind: "resolved", resolved } });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.setState({ ...this.state, phase: { kind: "error", message } });
    }
  }

  /** Enqueues only the resolved payable merchants, never the raw textarea. */
  async confirm(): Promise<void> {
    if (this._disposed) this.rearm();
    const phase = this.state.phase;
    if (phase.kind !== "resolved") return;

    const payable = phase.resolved.candidates
      .filter(
        (c) => c.disposition === "will_pay" || c.disposition === "will_revive",
      )
      .map((c) => c.email);

    // Derived from the reviewed set rather than read back off the checkbox, for
    // the same reason the emails are: the job must run under the terms the
    // operator actually approved. The runner re-resolves every merchant, so
    // without this a previewed `will_revive` becomes `wrong_stage` at execution
    // time and the run pays nobody.
    const reviveClosedTracks = phase.resolved.candidates.some(
      (c) => c.disposition === "will_revive",
    );

    if (payable.length === 0) {
      this.setState({
        ...this.state,
        phase: {
          kind: "error",
          message: "Nothing to pay — no resolved merchant is owed the bonus.",
        },
      });
      return;
    }

    this.setState({
      ...this.state,
      phase: { kind: "kicking_off", resolved: phase.resolved },
    });

    try {
      const jobId = await this.repo.kickoff(payable, reviveClosedTracks);
      this._attach(
        {
          id: jobId,
          type: JOB_TYPE,
          status: "pending",
          triggeredBy: "",
          total: payable.length,
          processed: 0,
          counters: null,
          result: null,
          error: null,
          startedAt: null,
          completedAt: null,
        },
        phase.resolved,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.setState({ ...this.state, phase: { kind: "error", message } });
    }
  }

  /**
   * Returns to the email form. Refused while a run is still live, so a dropped
   * subscription cannot become a route to enqueueing a second payout over the
   * same merchants.
   */
  backToCompose(): void {
    if (this._activeJob()) return;
    this._detach();
    this.setState({ ...this.state, phase: { kind: "composing" } });
  }

  /** The job this sheet is still responsible for, if it has not settled. */
  private _activeJob(): JobRecord | null {
    const phase = this.state.phase;
    const job =
      phase.kind === "watching" || phase.kind === "cancelling"
        ? phase.job
        : phase.kind === "error"
          ? phase.job
          : null;
    if (!job) return null;
    const settled =
      job.status === "complete" ||
      job.status === "failed" ||
      job.status === "cancelled";
    return settled ? null : job;
  }

  async cancel(): Promise<void> {
    const phase = this.state.phase;
    if (phase.kind !== "watching") return;
    this.setState({
      ...this.state,
      phase: { kind: "cancelling", job: phase.job, resolved: phase.resolved },
    });
    try {
      await this.repo.cancelJob(phase.job.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // Carries the job: the run is still out there, so this must not offer a
      // path back to the email form.
      this.setState({
        ...this.state,
        phase: { kind: "error", message, job: phase.job },
      });
    }
  }

  reset(): void {
    this._detach();
    this.setState({
      phase: { kind: "composing" },
      emailsInput: "",
      reviveClosedTracks: false,
    });
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private _attach(seed: JobRecord, resolved: ResolvedBackfill): void {
    // The sheet can close while a resume lookup or kickoff is still in flight;
    // subscribing after that leaks a live AWS subscription nothing will close.
    if (this._disposed) return;
    this._detach();
    this.setState({
      ...this.state,
      phase: { kind: "watching", job: seed, resolved },
    });

    // Tracks the freshest record so a subscription failure can report which job
    // is still running rather than dropping it.
    let latest = seed;

    this._unsubscribe = this.repo.subscribeToJob(
      seed.id,
      (job) => {
        latest = job;
        const current = this.state.phase;
        const isTerminal =
          job.status === "complete" ||
          job.status === "failed" ||
          job.status === "cancelled";
        if (current.kind === "cancelling" && !isTerminal) {
          this.setState({
            ...this.state,
            phase: { kind: "cancelling", job, resolved },
          });
          return;
        }
        this.setState({
          ...this.state,
          phase: { kind: "watching", job, resolved },
        });
      },
      (err) => {
        // The run itself is unaffected by losing the subscription — it keeps
        // paying. Carrying the job keeps the sheet from offering a route back
        // to the email form while that is true.
        this.setState({
          ...this.state,
          phase: { kind: "error", message: err.message, job: latest },
        });
      },
    );
  }

  private _detach(): void {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
  }

  get jobStatus(): JobStatus | null {
    const { phase } = this.state;
    if (phase.kind === "watching" || phase.kind === "cancelling") {
      return phase.job.status;
    }
    return null;
  }
}
